import type { UserProfile, SubscriptionTier } from '@/types/user'

/**
 * Post-signup onboarding checklist.
 *
 * A pure, tier-aware resolver: given a user's profile it returns the ordered
 * list of "get your account ready" steps for their subscription tier, each with
 * a live `done` flag computed from EXISTING profile fields (GST number, finance
 * source, Stripe Connect / bank details, storefront branding, phone verification,
 * nonprofit verification, …). The user's manual skip / finish / tick-off choices
 * live on `profile.onboarding` (owner-writable, no rules change) and can only
 * push a step to done — the auto-detected done-ness is always derived fresh.
 *
 * Both the first-login guided wizard and the account-overview checklist widget
 * render off this single source of truth. No I/O here — trivially testable.
 */

export interface OnboardingStep {
  id: string
  title: string
  description: string
  ctaLabel: string
  ctaRoute: string
  done: boolean
  /** Core "account isn't really ready without this" steps. Drives isOnboardingComplete. */
  required: boolean
}

export interface OnboardingProgress {
  total: number
  done: number
  remaining: number
  requiredRemaining: number
  /** 0–100 integer. 100 when there are no steps. */
  percent: number
}

// ---------- Auto-detection helpers (all pure, null-safe) ----------

function hasProfilePhoto(p: UserProfile): boolean {
  return !!(p.photoURL || p.tuiIcon)
}

function hasBranding(p: UserProfile): boolean {
  return !!(p.logoUrl || p.bannerUrl || (p.storeDescription && p.storeDescription.trim()))
}

function paymentConfigured(p: UserProfile): boolean {
  return (
    p.stripeConnectStatus === 'active' ||
    !!p.paymentAcceptance?.bankDetailsConfigured ||
    !!p.paymentAcceptance?.defaults?.cashOnPickup
  )
}

// A seller has "handled" finance once they've made an explicit choice — including
// deliberately turning it off ('none'). Undefined means they've never decided.
function financeChosen(p: UserProfile): boolean {
  return p.financeSource !== undefined
}

// ---------- Step catalogue ----------

interface StepDef {
  id: string
  title: string
  description: string
  ctaLabel: string
  ctaRoute: string
  required: boolean
  tiers: SubscriptionTier[]
  isDone: (p: UserProfile) => boolean
}

const BUSINESS: SubscriptionTier[] = ['storefront', 'dealership']
const SELLING: SubscriptionTier[] = ['storefront', 'dealership', 'nonprofit']
const EVERYONE: SubscriptionTier[] = ['general', 'storefront', 'dealership', 'nonprofit']

// Ordered — the resolver preserves this order per tier.
const STEP_CATALOGUE: StepDef[] = [
  {
    id: 'gst',
    title: 'Confirm your GST details',
    description: 'Add your GST number so the tax invoices your buyers receive show GST from day one.',
    ctaLabel: 'Add GST number',
    ctaRoute: '/profile/edit/gst',
    required: true,
    tiers: BUSINESS,
    isDone: (p) => !!p.gstNumber,
  },
  {
    id: 'branding',
    title: 'Set up your store branding',
    description: 'Add a logo, banner or a short description so your storefront looks the part.',
    ctaLabel: 'Edit store',
    ctaRoute: '/profile/edit',
    required: true,
    tiers: SELLING,
    isDone: hasBranding,
  },
  {
    id: 'store-url',
    title: 'Choose your store URL',
    description: 'Pick a memorable web address for your storefront so customers can find you.',
    ctaLabel: 'Choose URL',
    ctaRoute: '/profile/edit',
    required: false,
    tiers: BUSINESS,
    isDone: (p) => !!p.storeSlug,
  },
  {
    id: 'payment',
    title: 'Set up how you get paid',
    description: 'Connect a payout account or add bank / cash-on-pickup details so you can accept orders.',
    ctaLabel: 'Payment settings',
    ctaRoute: '/profile/settings/seller',
    required: true,
    tiers: SELLING,
    isDone: paymentConfigured,
  },
  {
    id: 'finance',
    title: 'Choose your finance option',
    description: 'Decide whether to show Good Lending, your own finance provider, or none on your listings.',
    ctaLabel: 'Finance settings',
    ctaRoute: '/profile/edit',
    required: false,
    tiers: ['dealership'],
    isDone: financeChosen,
  },
  {
    id: 'nonprofit-verify',
    title: 'Get your organisation verified',
    description: 'Add your charity number or school address so our team can verify your organisation.',
    ctaLabel: 'Update details',
    ctaRoute: '/profile/edit',
    required: true,
    tiers: ['nonprofit'],
    isDone: (p) => !!p.nonprofitVerified,
  },
  {
    id: 'profile-photo',
    title: 'Add a profile photo',
    description: 'Pick a Tui avatar or upload a photo so buyers and sellers recognise you.',
    ctaLabel: 'Add a photo',
    ctaRoute: '/profile/edit',
    required: false,
    tiers: ['general'],
    isDone: hasProfilePhoto,
  },
  {
    id: 'set-location',
    title: 'Set your region',
    description: 'Tell us where you are so your listings show the right location.',
    ctaLabel: 'Set region',
    ctaRoute: '/profile/edit',
    required: false,
    tiers: ['general'],
    isDone: (p) => !!p.location,
  },
  {
    id: 'verify-phone',
    title: 'Verify your phone number',
    description: 'Add and verify a phone number to help keep your account secure.',
    ctaLabel: 'Verify phone',
    ctaRoute: '/profile/settings',
    required: false,
    tiers: EVERYONE,
    isDone: (p) => !!p.phoneVerified,
  },
]

// ---------- Public API ----------

/** Ordered onboarding steps for this profile's tier. Empty for null/admin. */
export function resolveOnboardingSteps(profile: UserProfile | null): OnboardingStep[] {
  if (!profile) return []
  const tier = profile.subscriptionTier || 'general'
  if (tier === 'admin') return []

  const completedSteps = profile.onboarding?.completedSteps ?? []

  return STEP_CATALOGUE
    .filter((def) => def.tiers.includes(tier))
    .map((def) => ({
      id: def.id,
      title: def.title,
      description: def.description,
      ctaLabel: def.ctaLabel,
      ctaRoute: def.ctaRoute,
      required: def.required,
      // Manual completion can only push a step to done; auto-detection is live.
      done: def.isDone(profile) || completedSteps.includes(def.id),
    }))
}

/** Aggregate counts + a 0–100 integer percentage for progress UI. */
export function getOnboardingProgress(profile: UserProfile | null): OnboardingProgress {
  const steps = resolveOnboardingSteps(profile)
  const total = steps.length
  const done = steps.filter((s) => s.done).length
  const requiredRemaining = steps.filter((s) => s.required && !s.done).length
  return {
    total,
    done,
    remaining: total - done,
    requiredRemaining,
    percent: total === 0 ? 100 : Math.round((done / total) * 100),
  }
}

/**
 * True when there's nothing left the user MUST do — every required step is
 * satisfied, or they've explicitly finished the wizard, or the tier has no
 * steps (null/admin). Optional steps left undone don't block completeness.
 */
export function isOnboardingComplete(profile: UserProfile | null): boolean {
  if (!profile) return true
  if (profile.subscriptionTier === 'admin') return true
  if (profile.onboarding?.completed) return true
  return getOnboardingProgress(profile).requiredRemaining === 0
}

/**
 * Whether to auto-open the guided wizard for this profile. Fires once per fresh
 * account: as soon as it's dismissed (skipped) or completed it stays closed, and
 * it never opens when there's nothing outstanding. The overview checklist widget
 * ignores this gate — it always shows current progress.
 */
export function shouldShowOnboarding(profile: UserProfile | null): boolean {
  if (!profile) return false
  if (profile.subscriptionTier === 'admin') return false
  if (profile.onboarding?.dismissed || profile.onboarding?.completed) return false
  return getOnboardingProgress(profile).remaining > 0
}
